import Konva from "konva";
import { KonvaEventObject } from "konva/lib/Node";
import { Vector2d } from "konva/lib/types";
import React, { forwardRef, Ref } from "react";
import { Arrow, Ellipse, Line, Rect } from "react-konva";

import { CanvasAnnotationUpdate } from "types/imageAnnotations";

import { Point } from "../types";

import { EditableText } from "./EditableText/EditableText";

export type ShapeType = "rectangle" | "circle" | "line" | "arrow" | "text";

type ShapeRendererProps = {
  type: ShapeType;
  start: Point;
  end: Point;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  perfectDrawEnabled?: boolean;
  hitStrokeWidth?: number;
  fillEnabled?: boolean;
  shadowForStrokeEnabled?: boolean;
  draggable?: boolean;
  dragBoundFunc?: (pos: Vector2d) => Vector2d;
  onMouseDown?: (event: KonvaEventObject<MouseEvent>) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onDragMove?: (event: KonvaEventObject<DragEvent>) => void;
  text?: string;
  isSelected?: boolean;
  onClick?: (event: KonvaEventObject<MouseEvent>) => void;
  updateAnnotation?: (updatedBoundingBox: CanvasAnnotationUpdate) => void;
  fontSize?: number;
  imageZoomDifference?: number;
  onDblClick?: (event: KonvaEventObject<MouseEvent>) => void;
  isDoubleClicked?: boolean;
  shadowOffset?: Point;
  shadowBlur?: number;
  shadowOpacity?: number;
  shadowColor?: string;
};

function normalizeSquarePoints(p1: Point, p2: Point): { start: Point; end: Point } {
  const start: Point = {
    x: Math.min(p1.x, p2.x),
    y: Math.min(p1.y, p2.y),
  };

  const end: Point = {
    x: Math.max(p1.x, p2.x),
    y: Math.max(p1.y, p2.y),
  };

  return { start, end };
}

export type Bounds = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export type Direction = {
  horizontal: "left" | "right";
  vertical: "top" | "bottom";
};

export type NormalizedBox = {
  bounds: Bounds;
  startDirection: Direction;
};

export function normalizeBox(start: Point, end: Point): NormalizedBox {
  return {
    bounds: {
      left: Math.min(start.x, end.x),
      right: Math.max(start.x, end.x),
      top: Math.min(start.y, end.y),
      bottom: Math.max(start.y, end.y),
    },
    startDirection: {
      horizontal: start.x <= end.x ? "left" : "right",
      vertical: start.y <= end.y ? "top" : "bottom",
    },
  };
}

export function denormalizeBox(bounds: Bounds, startDirection: Direction): { start: Point; end: Point } {
  const start: Point = {
    x: bounds[startDirection.horizontal],
    y: bounds[startDirection.vertical],
  };

  const end: Point = {
    x: bounds[startDirection.horizontal === "left" ? "right" : "left"],
    y: bounds[startDirection.vertical === "top" ? "bottom" : "top"],
  };

  return { start, end };
}

/**
 * Shapes that cannot be normalized into a square with the start and end points
 * since they require the points to have the direction the user drew them.
 */
export const SHAPE_TYPE_REQUIRING_DIRECTION: ShapeType[] = ["arrow", "line"];

export const ShapeRenderer = forwardRef<Konva.Shape, ShapeRendererProps>(
  (
    {
      type,
      text,
      isSelected,
      onClick,
      updateAnnotation,
      start,
      end,
      fontSize,
      imageZoomDifference = 0,
      isDoubleClicked,
      ...commonProps
    },
    ref
  ) => {
    const normalizedSquare = normalizeSquarePoints(start, end);

    // width and size with a minimum of 1 for the case when an arrow is drawn in 0, 90, 180 or 270 degrees
    const width = Math.max(1, normalizedSquare.end.x - normalizedSquare.start.x);
    const height = Math.max(1, normalizedSquare.end.y - normalizedSquare.start.y);

    const mergedCommonProps = {
      ...commonProps,
      x: normalizedSquare.start.x,
      y: normalizedSquare.start.y,
      width,
      height,
    };

    const points: [number, number, number, number] = [
      start.x - normalizedSquare.start.x,
      start.y - normalizedSquare.start.y,
      end.x - normalizedSquare.start.x,
      end.y - normalizedSquare.start.y,
    ];

    // invalid shapes with a width or height of 0 cannot be rendered
    if (width <= 0 || height <= 0) {
      return null;
    }

    if (type === "rectangle") {
      return <Rect {...mergedCommonProps} ref={ref as Ref<Konva.Rect>} />;
    }

    if (type === "circle") {
      return (
        <Ellipse
          {...mergedCommonProps}
          ref={ref as Ref<Konva.Ellipse>}
          offsetX={-mergedCommonProps.width / 2}
          offsetY={-mergedCommonProps.height / 2}
          radiusX={mergedCommonProps.width / 2}
          radiusY={mergedCommonProps.height / 2}
        />
      );
    }

    if (type === "line") {
      return <Line {...mergedCommonProps} ref={ref as Ref<Konva.Line>} points={points} />;
    }

    if (type === "arrow") {
      return (
        <Arrow
          {...mergedCommonProps}
          ref={ref as Ref<Konva.Arrow>}
          fill={commonProps.stroke}
          perfectDrawEnabled
          fillEnabled
          points={points}
        />
      );
    }

    if (type === "text") {
      return (
        <EditableText
          {...mergedCommonProps}
          fill={commonProps.stroke}
          text={text}
          isEditing={isDoubleClicked}
          fontSize={fontSize}
          // Text does not require constant size while zooming
          zoom={imageZoomDifference}
          onTextChange={(newText) => {
            updateAnnotation?.({
              text: newText,
            });
          }}
        />
      );
    }

    return null;
  }
);
